import { Typography, TextField, Button, CircularProgress, Stack, FormControl, InputLabel, Select, MenuItem, FormHelperText, Checkbox, ListSubheader } from "@mui/material";
import { CadastrarProgramaFidelidadeForm } from "./styles";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { programaFidelidadeSchema } from "../../../../schemas/programaFidelidadeSchema";
import { FieldValues } from "react-hook-form/dist/types";
import { useEffect, useMemo } from "react";
import { TipoProgramaFidelidade, TipoProgramaFidelidadeDescricao } from "../../../../enum/TipoProgramaFidelidade";
import { NumericFormat } from 'react-number-format';
import * as yup from "yup";
import { useFetchCategorias } from "../../../../../produtos/hooks/useFetchCategorias";
import { ICategoria } from "../../../../../produtos/interfaces/ICategoria";
import { IProduto } from "../../../../../produtos/interfaces/IProduto";

interface CadastrarEditarProgramaFidelidadeProps {
  cadastrar: (programa: FieldValues) => void;
  cancelar: () => void;
  loading: boolean;
}

type ProgramaFidelidadeFormData = yup.InferType<typeof programaFidelidadeSchema>;

export const CadastrarEditarProgramaFidelidade = ({ cadastrar, cancelar, loading }: CadastrarEditarProgramaFidelidadeProps) => {
  const { control, setValue, register, handleSubmit, watch, reset, formState: { errors } } = useForm<ProgramaFidelidadeFormData>({
    resolver: yupResolver(programaFidelidadeSchema),
    mode: "onChange",
    defaultValues: {
      tipo: undefined,
      porcentagemRetorno: undefined,
      produtoUuids: [],
      quantidadePedidosNecessarios: undefined,
      diasVencimentoCashback: undefined,
      limiteAcumuloCashback: undefined
    }
  });

  const { categorias, isLoading: loadingCategorias } = useFetchCategorias();
  const tipoValue = watch("tipo");
  const isCashback = tipoValue === TipoProgramaFidelidade.CASHBACK;
  const isItens = tipoValue === TipoProgramaFidelidade.ITENS;

  // Flatten produtos from categorias
  const produtosDisponiveis = useMemo(() => {
    if (!categorias) return [];
    const produtos: IProduto[] = [];
    categorias.forEach((categoria: ICategoria) => {
      if (categoria.produtos) {
        categoria.produtos.forEach((produto: IProduto) => {
          produtos.push(produto);
        });
      }
    });
    return produtos;
  }, [categorias]);

  // Get produtos grouped by categoria for display
  const produtosPorCategoria = useMemo(() => {
    if (!categorias) return [];
    return categorias.filter((cat: ICategoria) => cat.produtos && cat.produtos.length > 0);
  }, [categorias]);


  // Reset conditional fields when tipo changes
  useEffect(() => {
    if (tipoValue === TipoProgramaFidelidade.CASHBACK) {
      setValue("produtoUuids", []);
      setValue("quantidadePedidosNecessarios", undefined);
    } else if (tipoValue === TipoProgramaFidelidade.ITENS) {
      setValue("porcentagemRetorno", undefined);
      setValue("diasVencimentoCashback", undefined);
      setValue("limiteAcumuloCashback", undefined);
    }
  }, [tipoValue, setValue]);

  const handleCancelar = () => {
    reset({
      tipo: undefined,
      porcentagemRetorno: undefined,
      produtoUuids: [],
      quantidadePedidosNecessarios: undefined,
      diasVencimentoCashback: undefined,
      limiteAcumuloCashback: undefined
    });
    cancelar();
  }

  const handleSubmitWithError = handleSubmit(
    async (programa: FieldValues) => {
      try {
        // Prepare payload based on tipo
        const payload: any = {
          tipo: programa.tipo
        };

        if (programa.tipo === TipoProgramaFidelidade.CASHBACK) {
          payload.porcentagemRetorno = programa.porcentagemRetorno;
          payload.diasVencimentoCashback = programa.diasVencimentoCashback ?? null;
          payload.limiteAcumuloCashback = programa.limiteAcumuloCashback ?? null;
        } else if (programa.tipo === TipoProgramaFidelidade.ITENS) {
          payload.produtoUuids = programa.produtoUuids || [];
          payload.quantidadePedidosNecessarios = programa.quantidadePedidosNecessarios;
        }

        await cadastrar(payload);
      } catch (error) {
        console.error("Erro ao processar formulário:", error);
      }
    },
    (errors) => {
      console.log("Erros de validação:", errors);
    }
  );

  const renderProdutosValue = (selected: string[]) => {
    if (selected.length === 0) return "";
    if (selected.length === 1) {
      const produto = produtosDisponiveis.find(p => p.uuid === selected[0]);
      return produto?.descricao || selected[0];
    }
    return `${selected.length} produtos selecionados`;
  };

  return (
    <CadastrarProgramaFidelidadeForm onSubmit={handleSubmitWithError}>
      <Typography variant="h6">
        Cadastro de programa de fidelidade
      </Typography>
      
      <FormControl fullWidth style={{ margin: "0.5rem 0" }} error={!!errors.tipo} size="small">
        <InputLabel>Tipo*</InputLabel>
        <Select
          {...register("tipo")}
          label="Tipo*"
          value={watch("tipo") || ""}
        >
          <MenuItem value={TipoProgramaFidelidade.CASHBACK}>
            {TipoProgramaFidelidadeDescricao[TipoProgramaFidelidade.CASHBACK]}
          </MenuItem>
          <MenuItem value={TipoProgramaFidelidade.ITENS}>
            {TipoProgramaFidelidadeDescricao[TipoProgramaFidelidade.ITENS]}
          </MenuItem>
        </Select>
        {errors.tipo && <FormHelperText>{errors.tipo.message as string}</FormHelperText>}
        {tipoValue && (
          <FormHelperText sx={{ mt: 1 }}>
            {tipoValue === TipoProgramaFidelidade.CASHBACK 
              ? "O cliente recebe uma porcentagem do valor do pedido como retorno."
              : "O cliente recebe itens após completar uma quantidade de pedidos."}
          </FormHelperText>
        )}
      </FormControl>

      {isCashback && (
        <>
          <Controller
            name="porcentagemRetorno"
            control={control}
            render={({ field }) => (
              <NumericFormat
                customInput={TextField}
                label="Porcentagem de Retorno*"
                style={{ width: "100%", margin: "0.5rem 0" }}
                size="small"
                value={field.value || ""}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  field.onChange(numValue !== undefined ? numValue : "");
                }}
                error={!!errors.porcentagemRetorno}
                helperText={errors.porcentagemRetorno?.message as string}
                suffix="%"
                decimalScale={2}
                allowNegative={false}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return floatValue === undefined || (floatValue >= 0.01 && floatValue <= 100);
                }}
              />
            )}
          />

          <Controller
            name="diasVencimentoCashback"
            control={control}
            render={({ field }) => (
              <TextField
                label="Dias para vencimento do cashback (opcional)"
                type="number"
                style={{ width: "100%", margin: "0.5rem 0" }}
                size="small"
                value={field.value ?? ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? null : parseInt(e.target.value, 10);
                  field.onChange(value);
                }}
                error={!!errors.diasVencimentoCashback}
                helperText={errors.diasVencimentoCashback?.message as string || "Deixe em branco para cashback sem prazo de vencimento"}
                inputProps={{ min: 1, step: 1 }}
              />
            )}
          />

          <Controller
            name="limiteAcumuloCashback"
            control={control}
            render={({ field }) => (
              <NumericFormat
                customInput={TextField}
                label="Limite máximo de acúmulo em R$ (opcional)"
                style={{ width: "100%", margin: "0.5rem 0" }}
                size="small"
                value={field.value ?? ""}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  field.onChange(numValue !== undefined ? numValue : null);
                }}
                error={!!errors.limiteAcumuloCashback}
                helperText={errors.limiteAcumuloCashback?.message as string || "Deixe em branco para não limitar o saldo acumulado"}
                prefix="R$ "
                decimalScale={2}
                allowNegative={false}
              />
            )}
          />
        </>
      )}

      {isItens && (
        <>
          <Controller
            name="quantidadePedidosNecessarios"
            control={control}
            render={({ field }) => (
              <TextField
                label="Quantidade de Pedidos Necessários*"
                type="number"
                style={{ width: "100%", margin: "0.5rem 0" }}
                size="small"
                value={field.value || ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? undefined : parseInt(e.target.value, 10);
                  field.onChange(value);
                }}
                error={!!errors.quantidadePedidosNecessarios}
                helperText={errors.quantidadePedidosNecessarios?.message as string}
                inputProps={{ min: 1, step: 1 }}
              />
            )}
          />

          <Controller
            name="produtoUuids"
            control={control}
            defaultValue={[]}
            render={({ field }) => {
              const selectedUuids = field.value || [];
              
              return (
                <FormControl fullWidth style={{ margin: "0.5rem 0" }} error={!!errors.produtoUuids} size="small">
                  <InputLabel>Produtos*</InputLabel>
                  <Select
                    multiple
                    label="Produtos*"
                    value={selectedUuids}
                    onChange={(e) => {
                      const newValue = e.target.value as string[];
                      field.onChange(newValue);
                    }}
                    renderValue={renderProdutosValue}
                    disabled={loadingCategorias}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 250,
                        },
                      },
                    }}
                  >
                    {produtosPorCategoria.flatMap((categoria: ICategoria) => [
                      <ListSubheader key={`header-${categoria.uuid}`}>
                        {categoria.descricao}
                      </ListSubheader>,
                      ...(categoria.produtos || []).map((produto: IProduto) => (
                        <MenuItem key={produto.uuid} value={produto.uuid}>
                          <Checkbox checked={selectedUuids.includes(produto.uuid)} />
                          {produto.descricao}
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                  {errors.produtoUuids && <FormHelperText>{errors.produtoUuids?.message as string}</FormHelperText>}
                  {selectedUuids.length > 0 && (
                    <FormHelperText sx={{ mt: 1 }}>
                      {selectedUuids.length} produto{selectedUuids.length !== 1 ? 's' : ''} selecionado{selectedUuids.length !== 1 ? 's' : ''}
                    </FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
        </>
      )}

      <Stack flexDirection={"row"} sx={{ width: "100%", mt: 2 }} justifyContent={"space-between"}>
        <Button
          variant="outlined"
          style={{ width: "47%" }}
          type="button"
          onClick={handleCancelar}
        >
          CANCELAR
        </Button>
        <Button
          variant="contained"
          style={{ width: "47%" }}
          type="submit"
          disabled={loading}
        >
          {loading ? <CircularProgress color="inherit" size={20} /> : "CADASTRAR"}
        </Button>
      </Stack>
    </CadastrarProgramaFidelidadeForm>
  );
};

